import { useChatSessionUsageQuery } from "@qw/graphql";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import { useEffect } from "react";

import { useChat } from "../../providers";

export const ChatContextUsageIndicator = () => {
  const { chatSession, onStreamComplete } = useChat();

  // Fetch usage data
  const { data, refetch } = useChatSessionUsageQuery({
    variables: { id: chatSession.id },
  });

  // Subscribe to stream completion events to refetch usage data
  useEffect(() => {
    return onStreamComplete(() => {
      void refetch();
    });
  }, [onStreamComplete, refetch]);

  const usage = data?.chatSessionUsage;

  // Don't render if no token data available
  if (!usage || usage.lastInputTokens === 0) {
    return null;
  }

  // lastInputTokens represents the actual conversation size from the most recent request
  // This is the meaningful metric to compare against the context window limit
  const lastInputTokens = usage.lastInputTokens;
  const limit = usage.contextLimit;

  const percentage = Math.min((lastInputTokens / limit) * 100, 100);

  // SVG circle geometry
  const size = 14;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percentage / 100);

  // Color based on usage: success < 50%, qw-yellow 50-80%, destructive > 80%
  const isWarning = percentage >= 50 && percentage < 80;
  const isError = percentage >= 80;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <svg
          width={size}
          height={size}
          aria-label={`Context usage: ${percentage.toFixed(1)}%`}
          className="-rotate-90 cursor-pointer mx-1"
        >
          {/* Background track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            className="stroke-foreground/10"
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={cn("transition-all duration-300 stroke-muted-foreground", {
              "stroke-destructive": isError,
              "stroke-qw-yellow": isWarning,
            })}
          />
        </svg>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs">
          <div>
            {lastInputTokens.toLocaleString()} / {limit.toLocaleString()} tokens
          </div>
          <div className="text-muted-foreground">{percentage.toFixed(1)}% used</div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
